import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject, interval } from 'rxjs';
import { finalize, takeUntil, map, startWith } from 'rxjs/operators';

// CONFIGS
import { CategoryService } from '@app/core/products/services/category.service';
import { SubCategoryService } from '@app/core/products/services/subcategory.service';

import { SnackBarConfig } from '../../../shared/configs/snackbar.config';

// SERVICES
import { AppIntegrationAccountProductsApiService } from '../../../shared/services/api/app-integration/app-integration-account-products-api.service';

@Component({
  selector: 'lum-app-integration-account-bulk-create-products-modal',
  templateUrl:
    './app-integration-account-bulk-create-products-modal.component.html',
  styleUrls: [
    './app-integration-account-bulk-create-products-modal.component.scss',
  ],
})
export class AppIntegrationAccountBulkCreateProductsModalComponent
  implements OnInit, OnDestroy
{
  form :UntypedFormGroup;
  loading = true;
  submitting = false;

  selectedCategory :any = null;

  readonly #destroy$ = new Subject<void>();

  constructor(
    private dialogRef :MatDialogRef<AppIntegrationAccountBulkCreateProductsModalComponent>,
    private fb :UntypedFormBuilder,
    private snackBar :MatSnackBar,
    private appIntegrationAccountProductsApiService :AppIntegrationAccountProductsApiService,
    public productCategoriesSvc :CategoryService,
    public productSubCategoriesSvc :SubCategoryService,
    @Inject(MAT_DIALOG_DATA) public data :{ appIntegrationAccountId :number },
  ) {}

  ngOnInit() :void {
    this.#initForm();
    this.#loadFormData();
    this.#monitorLoadingState();
  }

  ngOnDestroy() :void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  #initForm() :void {
    this.form = this.fb.group({
      appIntegrationAccountId: [
        this.data.appIntegrationAccountId,
        Validators.required,
      ],
      productIds: [[]],
      defaultValues: this.fb.group({
        category_id: [null, Validators.required],
        subcategory_id: [null, Validators.required],
      }),
      enableJobStatusTracking: [true],
    });
  }

  #loadFormData() :void {
    // Ensure categories and subcategories are loaded
    this.productCategoriesSvc.getProductCategories();
    this.productSubCategoriesSvc.get();
  }

  #monitorLoadingState() :void {
    // Check loading state every 100ms
    interval(100)
      .pipe(
        startWith(0),
        map(
          () =>
            this.productCategoriesSvc.gettingRecords ||
            this.productSubCategoriesSvc.gettingRecords,
        ),
        takeUntil(this.#destroy$),
      )
      .subscribe((isLoading) => {
        this.loading = isLoading;
      });
  }

  onCategoryChange(categoryId :number) :void {
    if (!categoryId) {
      this.form.get('defaultValues.subcategory_id').setValue(null);
      this.selectedCategory = null;

      return;
    }

    this.selectedCategory =
      this.productCategoriesSvc.getProductCategoryById(categoryId)[0];

    // Reset the subcategory value when category changes
    this.form.get('defaultValues.subcategory_id').setValue(null);
  }

  onSubmit() :void {
    if (this.form.invalid) {
      this.snackBar.open('Please fill in all required fields.', 'Error', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    this.submitting = true;

    // Ensure job tracking is enabled
    const formData = this.form.value;
    formData.enableJobStatusTracking = true;

    this.appIntegrationAccountProductsApiService
      .autoCreateUnmappedProducts(formData)
      .pipe(
        takeUntil(this.#destroy$),
        finalize(() => (this.submitting = false)),
      )
      .subscribe({
        next: (response) => {
          if (response && response.success) {
            this.snackBar.open(
              response.message || 'Products creation job started successfully.',
              'Success',
              { duration: SnackBarConfig.DEFAULT.duration },
            );
            this.dialogRef.close(true);
          } else {
            this.snackBar.open(
              response.message || 'Failed to start product creation job.',
              'Error',
              { duration: SnackBarConfig.DEFAULT.duration },
            );
          }
        },
        error: (error) => {
          this.snackBar.open(
            error.message || 'Failed to start product creation job.',
            'Error',
            { duration: SnackBarConfig.DEFAULT.duration },
          );
        },
      });
  }

  onCancel() :void {
    this.dialogRef.close();
  }
}
